/**
 * U0 · where a value came from. One component, everywhere a value came from somewhere.
 *
 *   CITED      extracted from a document, with the clause it was taken from
 *   DEFAULTED  from the library or a category profile
 *   INFERRED   the model proposed it and nobody has confirmed it
 *   RULED      a person decided, with who, when and why
 *
 * `INFERRED` is the one that has to be loud. An inferred value that nobody has individually
 * confirmed is a **blocking** readiness finding — it must not reach EFFECTIVE by inattention
 * (C1 §102, check C-4). So it reads "needs a person", not "inferred": the word says what to
 * do, not what happened.
 */
import React from "react";
import { Tooltip } from "antd";

const MEANING = {
  CITED: "Taken from a document. The clause it came from travels with it.",
  DEFAULTED: "From the library or a category profile — nobody chose it for this record.",
  INFERRED: "The model proposed this. Until a person confirms it, the record cannot become effective.",
  RULED: "A person decided this, and the record says who and when.",
};

export default function ProvenanceBadge({ value, sourceRef, ruledBy, ruledOn, compact }) {
  if (!value) return null;
  const key = String(value).toUpperCase();
  const label = key === "INFERRED" ? "needs a person" : key;

  const detail = [
    MEANING[key],
    key === "CITED" && sourceRef ? `Cited: ${sourceRef}` : null,
    key === "RULED" && ruledBy ? `Ruled by ${ruledBy}${ruledOn ? ` on ${ruledOn}` : ""}` : null,
  ].filter(Boolean).join(" ");

  return (
    <Tooltip title={detail} overlayStyle={{ maxWidth: 340 }}>
      <span className={`u0-prov ${key.toLowerCase()} ${compact ? "compact" : ""}`}>
        {label}
      </span>
    </Tooltip>
  );
}
